import type { EventPlacement, ZoneGeometry } from './types'

/** Nominal wingspan (ft) used to estimate variable-pitch zone capacity. */
export const NOMINAL_WINGSPAN_FT = 50

/**
 * Slot extent (ft along the lane) a zone reserves per aircraft. Uniform pitch
 * uses the designated fixed-slot box (authored size cap); variable pitch falls
 * back to a nominal footprint for estimation. Mirrors `placeLane`.
 */
export function slotExtentForZone(zone: ZoneGeometry, nominal = NOMINAL_WINGSPAN_FT): number {
  if (zone.pitchMode === 'uniform') {
    const designated = zone.progression === 'side_by_side' ? zone.maxWingspanFt : zone.maxLengthFt
    if (designated && designated > 0) return designated
  }
  return nominal
}

/**
 * Estimated slot capacity of a zone — how many nominal aircraft fit. Uses the
 * same cursor math as `placeLane` (first slot a margin in, then extent+margin
 * per slot) × lanes.
 */
export function estimateZoneCapacity(zone: ZoneGeometry, nominal = NOMINAL_WINGSPAN_FT): number {
  const extent = slotExtentForZone(zone, nominal)
  const m = zone.safetyMarginFt
  if (zone.width < m + extent) return 0
  const perLane = Math.floor((zone.width - m - extent) / (extent + m)) + 1
  return Math.max(0, perLane) * Math.max(1, zone.lanes)
}

export interface ConcurrencyPoint {
  t: number
  count: number
}

/**
 * Count placements parked (arrival ≤ t < departure) at each bucket across the
 * window — the demand-over-time curve whose PEAK is the real capacity ceiling.
 */
export function concurrencyHistogram(
  placements: EventPlacement[],
  windowStart: number,
  windowEnd: number,
  buckets = 240
): ConcurrencyPoint[] {
  const span = windowEnd - windowStart
  const step = span / buckets
  const out: ConcurrencyPoint[] = []
  for (let i = 0; i < buckets; i++) {
    const t = windowStart + i * step
    let count = 0
    for (const p of placements) if (p.arrival <= t && t < p.departure) count++
    out.push({ t, count })
  }
  return out
}

export function peakConcurrency(hist: ConcurrencyPoint[]): ConcurrencyPoint {
  return hist.reduce((best, p) => (p.count > best.count ? p : best), { t: 0, count: 0 })
}
